import PropTypes from 'prop-types';
import { Component, Children, cloneElement } from 'react';
import styles from './Binder.scss';
import Sidebar from 'binder/components/Sidebar/Sidebar';
import lodash from 'lodash';
import { connect } from 'react-redux';
import BinderNavigation from 'binder/components/BinderNavigation/BinderNavigation';

import {
    binderValidationFeatureAccess,
    doaFeatureAccess,
    binderEventFeatureAccess,
} from 'common/features/predicates';

import {
    binderSetupTabPredicate,
    defaultBinderSetupPath,
} from 'common/permissions/predicates';

import { binderRouteSelector, homeRouteSelector } from 'common/features/selectors';

import { manageTasksSelector } from 'common/permissions/selectors';

const SetupPathnameRegexp = /\/users|externalUsers|setup$/;

export function getPermittedTabs({
    userPermission,
    binderPermissions,
    binderId,
    doaAccess,
    hasValidationTab,
    hasEventsTab,
}) {
    let permittedTabs = [];

    if (hasValidationTab) {
        permittedTabs.push({
            label: 'Validation',
            path: `/binder/${binderId}/validation`
        });
    }

    permittedTabs.push({
        label: 'Documents',
        path: `/binder/${binderId}/files`
    });

    if (userPermission) {
        permittedTabs.push({
            label: 'Tasks',
            path: `/binder/${binderId}/tasks/`
        });
    }


    if (binderSetupTabPredicate({binderPermissions})) {
        permittedTabs.push({
            label: 'Setup',
            path: defaultBinderSetupPath({binderPermissions, binderId})
        });
    }

    if (doaAccess) {
        permittedTabs.push({
            label: 'Delegation',
            path: `/binder/${binderId}/delegation`
        });
    }

    if (hasEventsTab) {
        permittedTabs.push({
            label: 'Events',
            path: `/binder/${binderId}/events`
        });
    }

    return permittedTabs;
}

class Binder extends Component {
    constructor() {
        super(...arguments);
        this.lastPathname = '';
        this.contentStyle = '';
        this.setSidebarLinks = this.setSidebarLinks.bind(this);
        this.setSidebarIcons = this.setSidebarIcons.bind(this);
        this.setSidebarCollapsed = this.setSidebarCollapsed.bind(this);
        this.calculateFixedNav = this.calculateFixedNav.bind(this);
        this.state = {
            links: [],
            iconClass: '',
            isCollapsed: false,
            fixedNav : false,
        };
    }

    isSetupPage() {
        const nextPathname = this.props.location.pathname;
        const isSetupDetailsRoute = nextPathname === `/binder/${this.props.binderId}`;
        const isSetupPage = isSetupDetailsRoute || SetupPathnameRegexp.test(nextPathname);

        return this.lastPathname !== nextPathname && isSetupPage;
    }

    componentDidMount() {
        if (this.isSetupPage()) {
            this.setSetupSidebarLinks();
        }

        window.addEventListener('scroll', this.calculateFixedNav);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.calculateFixedNav);
    }

    calculateFixedNav() {
        const { fixedNav } = this.state;
        const TOP_NAVIGATION_HEIGHT_REM = 3.5;
        const currentPageOffsetRem = window.pageYOffset / 16;
        const hasScrolledPastTopNav = currentPageOffsetRem > TOP_NAVIGATION_HEIGHT_REM;

        if (hasScrolledPastTopNav && !fixedNav) {
            return this.setFixedNav(true);
        }

        if (!hasScrolledPastTopNav && fixedNav) {
            return this.setFixedNav(false);
        }

    }

    setFixedNav(isFixed) {
        this.setState({
            fixedNav: isFixed
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.isSetupPage()) {
            this.setSetupSidebarLinks();
        }

        this.lastPathname = this.props.location.pathname;
        if (!lodash.isEqual(prevProps.location.pathname, this.lastPathname)) {
            // BUGFIX: Changing tabs will not reset the window scroll position,
            // leaving users in a place they may not expect.

            window.scrollTo(0, 0);
        }
    }

    setSetupSidebarLinks() {
        const { binderId } = this.props;
        this.setSidebarLinks([{
            label: 'Details',
            action: `/binder/${binderId}`,
            kind: 'details'
        }, {
            label: 'Internal Users',
            action: `/binder/${binderId}/users`,
            kind: 'internalUsers'
        }, {
            label: 'External Users',
            action: `/binder/${binderId}/externalUsers`,
            kind: 'externalUsers'
        }, {
            label: 'Setup',
            action: `/binder/${binderId}/setup`,
            kind: 'editBinderDetails',
        }]);
    }

    setSidebarLinks(links) {
        if (!Array.isArray(links)) {
            return;
        }

        this.setState({
            links
        });
    }

    setSidebarIcons(iconClass) {
        this.setState({
            iconClass
        });
    }

    setSidebarCollapsed(isCollapsed) {
        this.setState({
            isCollapsed
        });
    }

    render() {
        const { links, iconClass, isCollapsed, fixedNav } = this.state;
        const {
            binder, loadBinder, binderId, router, location, homeRoute, isStudy,
            manageTasksPermission, binderPermissions, doaAccess,
            binderValidationAccess, binderEventsAccess,
        } = this.props;
        const { title } = binder;

        // Derive the tab list on every render so that tabs whose visibility
        // depends on async-loaded data (feature access, binder settings) appear
        // as soon as that data resolves — not only on the initial mount.
        const tabs = getPermittedTabs({
            userPermission: manageTasksPermission,
            binderPermissions,
            binderId,
            doaAccess,
            hasValidationTab: !!binder.hasBinderValidationTemplateEnabled && binderValidationAccess,
            hasEventsTab: binderEventsAccess,
        });
        const childElement = cloneElement(Children.only(this.props.children), {
            binderId,
            setSidebarLinks: this.setSidebarLinks,
            setSidebarIcons: this.setSidebarIcons,
            setSidebarCollapsed: this.setSidebarCollapsed,
            reloadBinder: loadBinder
        });
        const sidebarProps = {
            links,
            heading: 'You have selected binder',
            title,
            iconClass,
            isCollapsed,
            fixedNav,
        };

        if (fixedNav) {
            if (isCollapsed) {
                this.contentStyle = styles.stuckContentFullWidth;
            } else {
                this.contentStyle = styles.stuckContent;
            }
        } else {
            if (isCollapsed) {
                this.contentStyle = styles.contentFullWidth;
            } else {
                this.contentStyle = styles.content;
            }
        }
        return (
            <div className={styles.binderContainer}  data-cy="binder">
                <BinderNavigation title={title} tabs={tabs} router={router} location={location} fixedNav={fixedNav} homeRoute={homeRoute} isStudy={isStudy} binderId={binderId} />
                <div className={styles.binder}>
                    <Sidebar {...sidebarProps} />
                    <div className={this.contentStyle} >
                        {childElement}
                    </div>
                </div>
            </div>
        );
    }
}

Binder.contextTypes = {
    location: PropTypes.any
};

Binder.propTypes = {
    children: PropTypes.element.isRequired,
    loadBinder: PropTypes.func.isRequired
};


export default connect((state) => {
    const manageTasksPermission = manageTasksSelector(state);
    const binderPermissions = lodash.get(state, 'me.binderPermissions', {});
    const binderValidationAccess = binderValidationFeatureAccess(state);
    const doaAccess = doaFeatureAccess(state);
    const binderEventsAccess = binderEventFeatureAccess(state);
    const isStudy = !!lodash.get(state, 'routing.locationBeforeTransitions.query.studyId');
    const homeRoute = isStudy ? homeRouteSelector(state) : binderRouteSelector(state);

    return {
        manageTasksPermission,
        binderPermissions,
        manageTasksSelector,
        binderValidationAccess,
        doaAccess,
        binderEventsAccess,
        homeRoute,
        isStudy,
    };
})(Binder);
